import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCoins } from '../../context/CoinContext';
import { useBooking } from '../../context/BookingContext';
import TripPlanSelector from '../TripPlan/TripPlanSelector';
import { canUserCreateTripPlan } from '../../utils/tripPlanUtils';
import { canUserBook, checkCoinRequirement, getUpgradeMessage } from '../../utils/subscriptionUtils';
import { validateBookingAvailability, checkGirlAvailabilityForDate } from '../../utils/bookingValidation';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import CustomDatePicker from './CustomDatePicker';

const BookingModal = ({ isOpen, onClose, girlData, price }) => {
  const { currentUser } = useAuth();
  const { coins, deductCoins } = useCoins();
  const { createBooking } = useBooking();
  
  console.log('🔍 BookingModal rendered with:', {
    isOpen,
    girlData: girlData?.nickname,
    price,
    currentUser: currentUser?.email
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [duration, setDuration] = useState(1);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTripPlan, setSelectedTripPlan] = useState(null);
  const [showTripPlanSelector, setShowTripPlanSelector] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [currentBookings, setCurrentBookings] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState({
    isAvailable: true,
    message: '',
    conflictingBookings: []
  });
  const [validatingAvailability, setValidatingAvailability] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  // คำนวณราคาจาก girl profile
  const originalServicePrice = girlData?.servicePrice || girlData?.prices?.servicePrice || 1500;
  const girlCoinPrice = girlData?.coinPrice || girlData?.prices?.coinPrice || girlData?.prices?.base || 50;
  
  // คำนวณราคาหลังส่วนลด
  const calculateDiscountedPrice = (originalPrice, discountType, discountValue) => {
    if (discountType === 'none' || discountValue <= 0) {
      return originalPrice;
    }
    if (discountType === 'fixed') {
      return Math.max(0, originalPrice - discountValue);
    }
    if (discountType === 'percentage') {
      const discountAmount = (originalPrice * discountValue) / 100;
      return Math.max(0, originalPrice - discountAmount);
    }
    return originalPrice;
  };
  
  const discountedServicePrice = calculateDiscountedPrice(
    originalServicePrice,
    girlData?.discountType,
    girlData?.discountValue
  );
  
  const totalCost = girlCoinPrice * duration;
  const canCreateTripPlan = canUserCreateTripPlan(currentUser?.role);

  // Helper function to calculate end time
  const calculateEndTime = (startTime, duration) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + (duration * 60);
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  // โหลดข้อมูล subscription และ booking history
  useEffect(() => {
    if (isOpen && currentUser) {
      loadUserData();
    }
  }, [isOpen, currentUser]);

  // ตรวจสอบความพร้อมใช้งานเมื่อวันที่หรือเวลาเปลี่ยน
  useEffect(() => {
    if (selectedDate && selectedTime && girlData?.id) {
      validateAvailability();
    } else if (selectedDate && girlData?.id) {
      // Reset availability status เมื่อยังไม่ได้เลือกเวลา
      setAvailabilityStatus({
        isAvailable: true,
        message: '',
        conflictingBookings: []
      });
    }
  }, [selectedDate, selectedTime, duration, girlData?.id]);

  // ตรวจสอบความพร้อมใช้งานเมื่อเลือกวันที่
  useEffect(() => {
    if (selectedDate && girlData?.id) {
      checkDateAvailability();
    }
  }, [selectedDate, girlData?.id]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // โหลดข้อมูล subscription
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setUserSubscription(userData.subscription || 'free');
        
        // TODO: โหลดจำนวนการจองในเดือนนี้
        // const bookingsRef = collection(db, 'bookings');
        // const q = query(bookingsRef, 
        //   where('userId', '==', currentUser.uid),
        //   where('createdAt', '>=', getStartOfMonth()),
        //   where('status', 'in', ['pending', 'confirmed', 'completed'])
        // );
        // const bookingsSnap = await getDocs(q);
        // setCurrentBookings(bookingsSnap.size);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkDateAvailability = async () => {
    if (!selectedDate || !girlData?.id) {
      // Reset availability status เมื่อไม่มีข้อมูล
      setAvailabilityStatus({
        isAvailable: true,
        message: '',
        conflictingBookings: []
      });
      return;
    }

    try {
      const result = await checkGirlAvailabilityForDate(girlData.id, selectedDate);
      
      if (!result.isAvailable) {
        setAvailabilityStatus({
          isAvailable: false,
          message: result.reason,
          conflictingBookings: []
        });
      } else {
        // Reset availability status เมื่อวันที่ใช้ได้
        setAvailabilityStatus({
          isAvailable: true,
          message: '',
          conflictingBookings: []
        });
      }
    } catch (error) {
      console.error('Error checking date availability:', error);
      // Reset availability status เมื่อเกิด error
      setAvailabilityStatus({
        isAvailable: true,
        message: '',
        conflictingBookings: []
      });
    }
  };

  const validateAvailability = async () => {
    if (!selectedDate || !selectedTime || !girlData?.id) {
      setAvailabilityStatus({
        isAvailable: true,
        message: '',
        conflictingBookings: []
      });
      return;
    }

    try {
      setValidatingAvailability(true);
      
      const result = await validateBookingAvailability(
        girlData.id,
        selectedDate,
        selectedTime,
        duration
      );
      
      setAvailabilityStatus(result);
    } catch (error) {
      console.error('Error validating availability:', error);
      setAvailabilityStatus({
        isAvailable: false,
        message: 'เกิดข้อผิดพลาดในการตรวจสอบความพร้อมใช้งาน',
        conflictingBookings: []
      });
    } finally {
      setValidatingAvailability(false);
    }
  };

  const handleSelectTripPlan = (tripPlan) => {
    setSelectedTripPlan(tripPlan);
    setShowTripPlanSelector(false);
    
    // ตั้งค่า default values จาก Trip Plan
    if (tripPlan.tripDate) {
      setSelectedDate(tripPlan.tripDate);
    }
    
    // ตั้งค่าเวลา default (ถ้ามี)
    if (tripPlan.preferredTime) {
      setSelectedTime(tripPlan.preferredTime);
    }
    
    // ตั้งค่าชั่วโมง default (ถ้ามี)
    if (tripPlan.duration) {
      setDuration(tripPlan.duration);
    }
  };

  const handleRemoveTripPlan = () => {
    setSelectedTripPlan(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      alert('กรุณาเลือกวันที่และเวลา');
      return;
    }

    // ตรวจสอบความพร้อมใช้งานก่อน
    if (!availabilityStatus.isAvailable) {
      alert(`ไม่สามารถจองได้: ${availabilityStatus.message}`);
      return;
    }

    // ตรวจสอบสิทธิ์การจองตาม subscription
    const bookingStatus = canUserBook(currentUser.role, userSubscription, currentBookings);
    if (!bookingStatus.canBook) {
      alert(bookingStatus.reason);
      return;
    }

    // ตรวจสอบเหรียญ
    const coinStatus = checkCoinRequirement(totalCost, coins, currentUser.role, userSubscription);
    if (!coinStatus.hasEnoughCoins) {
      alert(coinStatus.message);
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔍 Booking data:', {
        girlId: girlData.id,
        girlData: girlData,
        girlCoinPrice: girlCoinPrice,
        bookingDetails: {
          date: selectedDate,
          time: selectedTime,
          duration: duration,
          message: message,
          tripPlanId: selectedTripPlan?.id || null,
          tripPlanName: selectedTripPlan?.tripName || null,
          tripPlanDetails: selectedTripPlan ? {
            tripDate: selectedTripPlan.tripDate,
            tripLocation: selectedTripPlan.tripLocation,
            tripTheme: selectedTripPlan.tripTheme,
            participantCount: selectedTripPlan.participantCount,
            requiredGirls: selectedTripPlan.requiredGirls,
            description: selectedTripPlan.description,
            specialRequirements: selectedTripPlan.specialRequirements
          } : null
        }
      });

      await createBooking(girlData.id, girlData, girlCoinPrice, {
        date: selectedDate,
        time: selectedTime,
        duration: duration,
        message: message,
        // Trip Plan reference
        tripPlanId: selectedTripPlan?.id || null,
        tripPlanName: selectedTripPlan?.tripName || null,
        tripPlanDetails: selectedTripPlan ? {
          tripDate: selectedTripPlan.tripDate,
          tripLocation: selectedTripPlan.tripLocation,
          tripTheme: selectedTripPlan.tripTheme,
          participantCount: selectedTripPlan.participantCount,
          requiredGirls: selectedTripPlan.requiredGirls,
          description: selectedTripPlan.description,
          specialRequirements: selectedTripPlan.specialRequirements
        } : null
      });
      
      console.log('🔍 Deducting coins:', totalCost);
      await deductCoins(totalCost);
      
      alert('ส่งคำขอจองคิวเรียบร้อยแล้ว!');
      onClose();
      
      // Reset form
      setSelectedDate('');
      setSelectedTime('');
      setDuration(1);
      setMessage('');
    } catch (error) {
      console.error('❌▸ Error creating booking:', error);
      console.error('❌▸ Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`เกิดข้อผิดพลาด: ${error.message || 'กรุณาลองใหม่อีกครั้ง'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">จองคิว {girlData?.nickname}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              วันที่ {selectedTripPlan && <span className="text-blue-600 text-xs">(จาก Trip Plan)</span>}
            </label>
            <div className="relative">
              {/* Desktop: HTML5 Date Input */}
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                disabled={selectedTripPlan && selectedTripPlan.tripDate}
                className={`hidden md:block w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  selectedTripPlan && selectedTripPlan.tripDate 
                    ? 'bg-gray-100 cursor-not-allowed border-gray-300' 
                    : !availabilityStatus.isAvailable && selectedDate
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                }`}
                required
              />
              
              {/* Mobile: Custom Date Button */}
              <button
                type="button"
                onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                disabled={selectedTripPlan && selectedTripPlan.tripDate}
                className={`md:hidden w-full p-2 border rounded-md text-left ${
                  selectedTripPlan && selectedTripPlan.tripDate 
                    ? 'bg-gray-100 cursor-not-allowed border-gray-300 text-gray-500' 
                    : !availabilityStatus.isAvailable && selectedDate
                      ? 'border-red-300 bg-red-50 text-red-700'
                      : 'border-gray-300 text-gray-700'
                }`}
              >
                {selectedDate ? (
                  <span className="flex items-center justify-between">
                    <span>{new Date(selectedDate).toLocaleDateString('th-TH', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                    <span className="text-gray-500">📅</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-between text-gray-500">
                    <span>เลือกวันที่</span>
                    <span>📅</span>
                  </span>
                )}
              </button>
              
              {/* Desktop: Calendar Button */}
              <button
                type="button"
                onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                disabled={selectedTripPlan && selectedTripPlan.tripDate}
                className="hidden md:block absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed"
              >
                📅
              </button>
            </div>
            {selectedDate && !availabilityStatus.isAvailable && (
              <p className="text-xs text-red-600 mt-1">
                ⚠️ {availabilityStatus.message}
              </p>
            )}
            
            {/* Custom Date Picker */}
            {showCustomDatePicker && (
              <div className="mt-3">
                <CustomDatePicker
                  selectedDate={selectedDate}
                  onDateSelect={(date) => {
                    setSelectedDate(date);
                    setShowCustomDatePicker(false);
                  }}
                  girlId={girlData?.id}
                  disabled={selectedTripPlan && selectedTripPlan.tripDate}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              เวลา {selectedTripPlan && selectedTripPlan.preferredTime && <span className="text-blue-600 text-xs">(แนะนำจาก Trip Plan)</span>}
            </label>
            <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">เลือกเวลา</option>
              <option value="09:00">09:00</option>
              <option value="10:00">10:00</option>
              <option value="11:00">11:00</option>
              <option value="12:00">12:00</option>
              <option value="13:00">13:00</option>
              <option value="14:00">14:00</option>
              <option value="15:00">15:00</option>
              <option value="16:00">16:00</option>
              <option value="17:00">17:00</option>
              <option value="18:00">18:00</option>
              <option value="19:00">19:00</option>
              <option value="20:00">20:00</option>
              <option value="21:00">21:00</option>
            </select>
            {selectedTripPlan && selectedTripPlan.preferredTime && (
              <p className="text-xs text-blue-600 mt-1">
                💡 แนะนำเวลา: {selectedTripPlan.preferredTime} (จาก Trip Plan)
              </p>
            )}
          </div>

          {/* Availability Status */}
          {selectedDate && selectedTime && (
            <div className={`p-3 rounded-md border ${
              availabilityStatus.isAvailable 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center">
                {validatingAvailability ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-sm text-gray-600">กำลังตรวจสอบความพร้อมใช้งาน...</span>
                  </div>
                ) : (
                  <>
                    <span className={`text-sm font-medium ${
                      availabilityStatus.isAvailable ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {availabilityStatus.isAvailable ? '✅' : '❌'} {availabilityStatus.message}
                    </span>
                  </>
                )}
              </div>
              
              {/* Show conflicting bookings if any */}
              {!availabilityStatus.isAvailable && availabilityStatus.conflictingBookings.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-red-700 font-medium mb-1">การจองที่ชนกัน:</p>
                  {availabilityStatus.conflictingBookings.map((booking, index) => (
                    <div key={index} className="text-xs text-red-600 bg-red-100 p-2 rounded mb-1">
                      <div>👤 {booking.userName} ({booking.userEmail})</div>
                      <div>🕐 {booking.bookingTime} - {calculateEndTime(booking.bookingTime, booking.duration)} ({booking.duration} ชม.)</div>
                      <div>📅 {new Date(booking.bookingDate).toLocaleDateString('th-TH')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ระยะเวลา (ชั่วโมง) {selectedTripPlan && selectedTripPlan.duration && <span className="text-blue-600 text-xs">(แนะนำจาก Trip Plan)</span>}
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={1}>1 ชั่วโมง</option>
              <option value={2}>2 ชั่วโมง</option>
              <option value={3}>3 ชั่วโมง</option>
              <option value={4}>4 ชั่วโมง</option>
              <option value={5}>5 ชั่วโมง</option>
              <option value={6}>6 ชั่วโมง</option>
              <option value={7}>7 ชั่วโมง</option>
              <option value={8}>8 ชั่วโมง</option>
            </select>
            {selectedTripPlan && selectedTripPlan.duration && (
              <p className="text-xs text-blue-600 mt-1">
                💡 แนะนำระยะเวลา: {selectedTripPlan.duration} ชั่วโมง (จาก Trip Plan)
              </p>
            )}
          </div>

          {/* Trip Plan Section */}
          {canCreateTripPlan && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🗺️ Trip Plan (ไม่บังคับ)
              </label>
              {selectedTripPlan ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900">{selectedTripPlan.tripName}</h4>
                      <p className="text-blue-700 text-sm">
                        {new Date(selectedTripPlan.tripDate).toLocaleDateString('th-TH')} • 
                        {selectedTripPlan.tripLocation}
                      </p>
                      <p className="text-blue-600 text-xs mt-1">
                        👥 {selectedTripPlan.participantCount} คน • 👩 {selectedTripPlan.requiredGirls} น้อง
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveTripPlan}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowTripPlanSelector(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition"
                >
                  ➕ เลือกหรือสร้าง Trip Plan
                </button>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ข้อความ (ไม่บังคับ)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="ข้อความถึงน้อง..."
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          {/* ค่าบริการจริง - เงินที่ต้องจ่ายหน้างาน */}
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
              💰 ค่าบริการจริง
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">ราคาเดิม:</span>
                <span className="text-gray-500 line-through">{originalServicePrice} บาท/ชม.</span>
              </div>
              {girlData?.discountType && girlData?.discountType !== 'none' && girlData?.discountValue > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ส่วนลด:</span>
                  <span className="text-red-600">
                    {girlData.discountType === 'fixed' 
                      ? `ลด ${girlData.discountValue} บาท`
                      : `ลด ${girlData.discountValue}%`
                    }
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">ราคาหลังส่วนลด:</span>
                <span className="text-green-600 font-semibold">{discountedServicePrice} บาท/ชม.</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">จำนวนชั่วโมง:</span>
                <span>{duration} ชั่วโมง</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-green-300 pt-2 mt-2">
                <span className="text-green-800">รวมค่าบริการ:</span>
                <span className="text-green-600">{(discountedServicePrice * duration).toLocaleString()} บาท</span>
              </div>
            </div>
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              💡 เงินนี้ต้องจ่ายหน้างานหรือมัดจำตามเงื่อนไขของน้อง
            </div>
          </div>

          {/* เหรียญระบบ - สำหรับจองคิว */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
              🪙 เหรียญระบบ
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">เหรียญต่อชั่วโมง:</span>
                <span className="text-blue-600 font-semibold">{girlCoinPrice} เหรียญ</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">จำนวนชั่วโมง:</span>
                <span>{duration} ชั่วโมง</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-blue-300 pt-2 mt-2">
                <span className="text-blue-800">รวมเหรียญ:</span>
                <span className="text-blue-600">{totalCost} เหรียญ</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span className="text-gray-600">เหรียญที่มี:</span>
                <span className={coins >= totalCost ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {coins.toLocaleString()} เหรียญ
                </span>
              </div>
              {coins < totalCost && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                  ⚠️ เหรียญไม่เพียงพอ ต้องเติมเหรียญเพิ่ม
                </div>
              )}
            </div>
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
              💡 เหรียญนี้ใช้สำหรับจองคิวผ่านระบบเท่านั้น
            </div>
          </div>
          
          {/* สถานะ Subscription */}
          {userSubscription && (
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg mb-4">
              <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center">
                👑 สถานะ Subscription
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Plan:</span>
                  <span className="text-purple-600 font-semibold capitalize">{userSubscription}</span>
                </div>
                {currentBookings > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">จองแล้วในเดือนนี้:</span>
                    <span className="text-purple-600 font-semibold">{currentBookings} ครั้ง</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ข้อความแจ้งเตือนทางกฎหมาย */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-blue-500 text-lg">📋</span>
                <h3 className="text-lg font-semibold text-blue-800">
                  ข้อกำหนดการใช้งาน
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowLegalDisclaimer(!showLegalDisclaimer)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {showLegalDisclaimer ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
              </button>
            </div>
            
            {showLegalDisclaimer && (
              <div className="text-sm text-blue-700 space-y-2 mb-3">
                <p className="font-medium">
                  ⚖️ การค้าประเวณีเป็นสิ่งผิดกฎหมายในประเทศไทย
                </p>
                <p>
                  • การใช้บริการนี้เพื่อวัตถุประสงค์ดังกล่าวถือเป็นการละเมิดกฎหมาย<br/>
                  • ผู้ฝ่าฝืนจะถูกดำเนินคดีตามกฎหมาย<br/>
                  • บริการนี้มีไว้เพื่อการพบปะสังสรรค์และกิจกรรมที่ถูกต้องตามกฎหมายเท่านั้น
                </p>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="legalAccept"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="legalAccept" className="text-sm text-blue-700">
                ฉันรับทราบและยอมรับข้อกำหนดการใช้งานข้างต้น
              </label>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading || coins < totalCost || !availabilityStatus.isAvailable || validatingAvailability || !legalAccepted}
              className="flex-1 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed relative"
              title="ส่งคำขอจองคิว"
            >
              {isSubmitting ? 'กำลังส่ง...' : 
               loading ? 'กำลังโหลด...' : 
               validatingAvailability ? 'กำลังตรวจสอบ...' :
               !availabilityStatus.isAvailable ? 'ไม่สามารถจองได้' :
               !legalAccepted ? 'กรุณายอมรับข้อกำหนด' :
               'ส่งจองคิว'}
            </button>
          </div>
        </form>
      </div>

      {/* Trip Plan Selector Modal */}
      {showTripPlanSelector && (
        <TripPlanSelector
          onSelectTripPlan={handleSelectTripPlan}
          onClose={() => setShowTripPlanSelector(false)}
          selectedTripPlan={selectedTripPlan}
        />
      )}
    </div>
  );
};

export default BookingModal;